// Global registry + orchestrator for image→3D generation jobs.
//
// Owning the pipeline OUTSIDE the content dialog is the point: the teacher can
// close the editing panel (or navigate) and the job keeps running — the map
// shows a small indicator, and re-opening the component re-attaches to the
// same job. Views never poll on their own; they subscribe here.
//
// Two pipelines, selected by Config.AI_3D_EFFECTIVE_MODE():
//
//   self  — this manager drives everything from the browser: claim the
//           component's ai_3d_gen_item row (status 100, so the server cron
//           skips it), submit the image to the self-hosted GPU platform, poll,
//           download the GLB, upload it to EV storage, write payload.img_url_3.
//           ANY failure flips the claim row to -1, which hands the component to
//           the deployed Hunyuan cron — the cloud fallback.
//
//   cloud — legacy behaviour: the server cron does the work; the manager only
//           watches component.payload until the GLB is reachable.
//
// Jobs persist to localStorage so a reload resumes watching/polling; a running
// self task is also recorded in payload.self3d so another machine could adopt.

import Config from "../Config.jsx";
import Localization from "../Localization.jsx";
import AuthService from "./AuthService.js";
import GlbOptimizer from "./GlbOptimizer.js";
import axios from "axios";

export const PHASE = {
  SUBMITTING: "submitting",        // sending the image to the self host
  QUEUED: "queued",                // self host accepted, waiting for the GPU
  PREPARING: "preparing",          // planning / preparing_environment
  GENERATING: "generating",        // GPU is building the model
  FINALIZING: "finalizing",        // verifying on the self host
  IMPORTING: "importing",          // downloading GLB + saving into EV storage
  CLOUD_GENERATING: "cloud",       // server-side Hunyuan pipeline is working
  DONE: "done",
  FAILED: "failed",
  CANCELLED: "cancelled",          // user cancelled; component keeps its image
};

const STORAGE_KEY = "ev3d_jobs_v1";
const SELF_POLL_MS = 10000;
const CLOUD_POLL_MS = 5000;
// The GPU runs ONE generation at a time, so on a busy day a job can sit
// queued for hours before its ~5-10 minutes of compute. Timeouts therefore
// have to be sized for the QUEUE, not the generation: anything shorter kills
// legitimately-waiting jobs and double-spends via the cloud fallback.
const SELF_TIMEOUT_MS = 6 * 60 * 60 * 1000;   // 6h wall clock on the self host
// Stall = the self host stopped changing while RUNNING (suspended while
// queued). Above the gateway's own 1h subprocess cap so a slow-but-alive
// paint pass is never mistaken for a hang; a truly wedged gateway still trips.
const STALL_TIMEOUT_MS = 70 * 60 * 1000;
const SLOW_AFTER_MS = 20 * 60 * 1000;         // UI hint only, both pipelines
const MAX_POLL_ERRORS = 10;                   // consecutive self-host poll failures
const IMPORT_ATTEMPTS = 3;                    // download/upload/save retries before falling back
const COMPONENT_CHECK_EVERY = 3;              // self poll verifies the component still exists every Nth tick
const CLOUD_POLL_SLOW_AFTER_MS = 60 * 60 * 1000;  // after 1h, watch politely...
const CLOUD_POLL_SLOW_MS = 30000;                  // ...every 30s instead of 5s
const CLOUD_WATCH_MAX_MS = 8 * 60 * 60 * 1000;     // 8h: stop watching, mark failed (retryable)
const CLOUD_ROW_CHECK_EVERY = 6;                   // cloud watch asks the server about the row every Nth tick

// ai_3d_gen_item status conventions (shared with the server cron):
// -1 queues the row for the Hunyuan pipeline; 100/105 are OURS — "self-host in
// progress" / "self-host done". Both sit outside the cron's status set, so a
// claimed row is invisible to it.
const GEN_ITEM_SELF_IN_PROGRESS = "100";
const GEN_ITEM_SELF_DONE = "105";
const GEN_ITEM_CANCELLED = "106";
const GEN_ITEM_QUEUE_FOR_CLOUD = "-1";

function now() {
  return Date.now();
}

function str(key, fallback) {
  const s = Localization && Localization.strings ? Localization.strings[key] : undefined;
  return s || fallback;
}

// The GPU platform narrates its own progress and names the model it is running
// ("Generating 3D asset with Hunyuan3D 2.1 (PBR)..."). Teachers see one generic
// localized line instead: which engine backs the built-in mode is ours to
// change, and substituting the whole string is safer than redacting names we
// would then have to keep in sync with whatever the platform ships next.
function publicSelfMessage(raw) {
  if (!raw) return "";
  return str("GEN3D_SELF_PROGRESS", "Generating the 3D asset with the built-in engine...");
}

class ThreeDJobManagerClass {
  constructor() {
    this.root = null;
    this.jobs = new Map();       // componentId(number) -> job
    this.listeners = new Set();
    this.timers = new Map();     // componentId -> setTimeout id
  }

  // ------------------------------------------------------------------ wiring

  /** Called once from RootPage after login; root supplies API + notifications. */
  init(root) {
    this.root = root;
    this.resume();
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  getJob(componentId) {
    return this.jobs.get(parseInt(componentId)) || null;
  }

  /** Active (not done/failed/cancelled) jobs, optionally scoped to one epack. */
  getActiveJobs(epackId = null) {
    const out = [];
    this.jobs.forEach((job) => {
      if (job.phase === PHASE.DONE || job.phase === PHASE.FAILED || job.phase === PHASE.CANCELLED) return;
      if (epackId != null && parseInt(job.epackId) !== parseInt(epackId)) return;
      out.push(job);
    });
    out.sort((a, b) => a.startedAt - b.startedAt);
    return out;
  }

  _notify(job) {
    this.listeners.forEach((fn) => {
      try {
        fn(job);
      } catch (e) {
        console.warn("3D job listener failed", e);
      }
    });
  }

  _set(job, patch) {
    Object.assign(job, patch, { lastChangeAt: now() });
    this.jobs.set(job.componentId, job);
    this._persist();
    this._notify(job);
  }

  _toast(message, ok) {
    if (this.root && this.root.addNotification) {
      try {
        this.root.addNotification(message, ok);
      } catch (e) { /* root unmounted */ }
    }
  }

  // ------------------------------------------------------------- persistence

  _persist() {
    try {
      const rows = [];
      this.jobs.forEach((j) => {
        if (j.phase === PHASE.DONE || j.phase === PHASE.FAILED || j.phase === PHASE.CANCELLED) return;
        rows.push({
          componentId: j.componentId, hotspotId: j.hotspotId, epackId: j.epackId,
          name: j.name, mode: j.mode, phase: j.phase, taskId: j.taskId || "",
          startedAt: j.startedAt, fellBack: !!j.fellBack,
        });
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
    } catch (e) { /* private mode: not persisting is fine */ }
  }

  /** Re-attach to jobs recorded before a reload. */
  resume() {
    let rows = [];
    try {
      rows = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch (e) {
      rows = [];
    }
    if (!Array.isArray(rows)) return;
    rows.forEach((row) => {
      if (!row || !row.componentId || this.jobs.has(parseInt(row.componentId))) return;
      const job = this._makeJob({
        componentId: row.componentId, hotspotId: row.hotspotId, epackId: row.epackId,
        name: row.name, mode: row.mode || "cloud",
      });
      job.startedAt = row.startedAt || now();
      job.taskId = row.taskId || "";
      job.fellBack = !!row.fellBack;
      this.jobs.set(job.componentId, job);
      if (job.mode === "self" && job.taskId && !job.fellBack) {
        this._set(job, { phase: PHASE.QUEUED, message: "" });
        this._selfPollLoop(job);
      } else {
        // Cloud job, a fallen-back job, or a self job that died before submit.
        // The last case may have left a claim row (status 100) blocking the
        // cron, so route it through the fallback flip; its claimed flag was
        // lost with the page, and _markGenItem's insert-then-update handling
        // is on the server side, so flag it claimed to force the PATCH first.
        if (job.mode === "self" && !job.taskId) {
          job.claimed = true;
          job.fellBack = true;
        }
        this._set(job, { phase: PHASE.CLOUD_GENERATING, message: "" });
        this._cloudWatchLoop(job);
      }
    });
    // The snapshot can be stale in ways the loops cannot always detect (a
    // task cancelled at the platform while no browser was open, a row parked
    // by another machine): reconcile everything once against server truth so
    // logging in shows the clean picture, not last session's.
    this.revalidate();
  }

  _makeJob({ componentId, hotspotId, epackId, name, mode }) {
    return {
      componentId: parseInt(componentId),
      hotspotId: hotspotId != null ? parseInt(hotspotId) : null,
      epackId: epackId != null ? parseInt(epackId) : null,
      name: name || "",
      mode,                       // "self" | "cloud" (pipeline the job STARTED on)
      phase: PHASE.SUBMITTING,
      progress: null,             // 0-100 from the self host, null = indeterminate
      message: "",                // provider progress text (shown small)
      error: "",
      taskId: "",
      fellBack: false,            // self job that switched to the cloud pipeline
      slow: false,
      claimed: false,             // ai_3d_gen_item row exists with status 100
      flipDone: false,            // fallback flip to -1 confirmed
      startedAt: now(),
      lastChangeAt: now(),
    };
  }

  _clearTimer(componentId) {
    const t = this.timers.get(componentId);
    if (t) {
      clearTimeout(t);
      this.timers.delete(componentId);
    }
  }

  _schedule(job, fn, ms) {
    this._clearTimer(job.componentId);
    this.timers.set(job.componentId, setTimeout(fn, ms));
  }

  // ------------------------------------------------------------- API helpers

  _api(cmd, reqObj) {
    return new Promise((resolve, reject) => {
      if (!this.root || !this.root.refs || !this.root.refs.API) {
        reject(new Error("API not ready"));
        return;
      }
      this.root.refs.API.request(cmd, reqObj, (res) => resolve(res), (err) => reject(err || new Error(cmd + " failed")));
    });
  }

  async _getComponent(componentId) {
    const res = await this._api("get_list", { table: "component", filter: { id: String(componentId) }, order_by: "id" });
    const list = res && res.body && Array.isArray(res.body.result) ? res.body.result : [];
    return list.length > 0 ? list[0] : null;
  }

  _parsePayload(raw) {
    if (raw == null) return {};
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(String(raw).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\n\r]/g, "")) || {};
    } catch (e) {
      return {};
    }
  }

  /** Read-merge-write payload keys on the component row (only payload is sent). */
  async _mergePayload(componentId, mutate) {
    const item = await this._getComponent(componentId);
    if (!item) throw new Error("component " + componentId + " not found");
    const payload = this._parsePayload(item.payload);
    mutate(payload);
    await this._api("update", {
      table: "component",
      where_field: "id",
      where_value: componentId,
      row: { payload: JSON.stringify(payload) },
    });
    return payload;
  }

  // GLB reachability probe (same rules the content dialog used): a half-written
  // blob answers HEAD with html/json/xml or zero bytes.
  async _probeModel(payload) {
    const img3 = payload && payload.img_url_3;
    if (!img3 || !img3.path) return false;
    try {
      const response = await fetch(Config.UPLOAD_PATH() + img3.path, { method: "HEAD", cache: "no-store" });
      if (!response.ok) return false;
      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      const contentLength = parseInt(response.headers.get("content-length") || "0", 10);
      if (contentType.includes("text/html") || contentType.includes("application/json") || contentType.includes("text/xml")) {
        return false;
      }
      if (!Number.isNaN(contentLength) && contentLength > 0) return true;
      return contentType === "" || contentType.includes("octet-stream") || contentType.includes("model/");
    } catch (e) {
      return false;
    }
  }

  // --------------------------------------------------------------- gen items

  _genItemParams(params) {
    const p = params || {};
    const tier = parseInt(p.tier) >= 1 && parseInt(p.tier) <= 3 ? parseInt(p.tier) : 1;
    const out = {
      tier,
      EnablePBR: p.pbr === false ? 0 : 1,
      FaceCount: parseInt(p.faceCount) > 0 ? parseInt(p.faceCount) : 500000,
      GenerateType: typeof p.generateType === "string" && p.generateType !== "" ? p.generateType : "Normal",
    };
    return out;
  }


  async _markGenItem(job, status, imgUrlAbs, params, extra) {
    const body = Object.assign({ component_id: job.componentId, status: status }, this._genItemParams(params), extra || {});
    if (imgUrlAbs) body.img_url_1 = imgUrlAbs;
    if (job.claimed) {
      await this._api("update_ai_3d_gen_item", body);
    } else {
      await this._api("do_insert_ai_3d_gen_item", body);
    }
  }

  // ------------------------------------------------------------ entry points

  /**
   * Start (or restart) the pipeline for a saved kind-16 component.
   * imageFileInfo is payload.img_url_1; params/prompt come from the form.
   * Replaces any job already tracked for this component.
   */
  start({ componentId, hotspotId, epackId, name, imageFileInfo, prompt, params }) {
    componentId = parseInt(componentId);
    const existing = this.jobs.get(componentId);
    if (existing) {
      this._clearTimer(componentId);
      if (existing.mode === "self" && existing.taskId && !existing.fellBack) {
        this._selfCancelRemote(existing); // fire and forget
      }
    }

    const mode = Config.AI_3D_EFFECTIVE_MODE();
    // Leave a breadcrumb for "why didn't my GPU get anything": the mode and
    // where it came from is the first gate of the whole pipeline.
    console.info(
      "[3D] start component", componentId, "mode:", mode,
      Config.AI_3D_MODE_FORCED_SELF() ? "(forced by Config.AI_3D_GEN_MODE)" : "(handshake ai3dGenMode: " + (Config._ai3dHandshake.mode || "absent → cloud") + ")",
      "— server-mediated (quota + submission via ev api)");
    const job = this._makeJob({ componentId, hotspotId, epackId, name, mode });
    this.jobs.set(componentId, job);
    this._set(job, {});

    if (mode === "self") {
      this._runSelfPipeline(job, imageFileInfo, prompt, params);
    } else {
      // Cloud mode: queue the gen item ourselves so the teacher's tier/PBR
      // choices are honoured (the cron's own discovery would use defaults);
      // if the route is missing the cron discovers the component anyway.
      const imgUrlAbs = imageFileInfo && imageFileInfo.path ? Config.UPLOAD_PATH() + imageFileInfo.path : "";
      this._api("do_insert_ai_3d_gen_item", Object.assign({
        component_id: job.componentId,
        status: GEN_ITEM_QUEUE_FOR_CLOUD,
        img_url_1: imgUrlAbs,
      }, this._genItemParams(params))).then(() => {
        job.claimed = true;
      }).catch(() => { /* older server: cron discovery covers it */ });
      this._set(job, { phase: PHASE.CLOUD_GENERATING });
      this._cloudWatchLoop(job);
    }
    return job;
  }

  /** Re-attach the panel to a component that may already be generating. */
  adopt({ componentId, hotspotId, epackId, name, payload }) {
    componentId = parseInt(componentId);
    if (this.jobs.has(componentId)) {
      const job = this.jobs.get(componentId);
      // Fill in locators the persisted row may have lacked.
      if (hotspotId != null && job.hotspotId == null) job.hotspotId = parseInt(hotspotId);
      if (epackId != null && job.epackId == null) job.epackId = parseInt(epackId);
      if (name) job.name = name;
      return job;
    }
    const p = payload || {};
    if (p.img_url_3 && p.img_url_3.path) return null; // already has a model
    if (!p.img_url_1 || !p.img_url_1.path) return null; // nothing to generate yet
    if (p.gen3d && p.gen3d.cancelled) return null; // user cancelled; only 重新生成 restarts it
    // An image alone no longer means a generation is running: since the 生成
    // 3D模型 button exists, a component can sit uploaded-but-not-started.
    // generate3DModel clears this marker in the same write that launches the
    // job, so only genuinely un-started components carry it.
    if (p.gen3d && p.gen3d.awaitingStart) return null;
    const selfTask = p.self3d && p.self3d.task_id;
    const job = this._makeJob({
      componentId, hotspotId, epackId, name,
      mode: selfTask ? "self" : "cloud",
    });
    if (selfTask) {
      job.taskId = p.self3d.task_id;
      job.claimed = true;
      job.startedAt = p.self3d.submitted_at || now();
      this.jobs.set(componentId, job);
      this._set(job, { phase: PHASE.QUEUED });
      this._selfPollLoop(job);
    } else {
      this.jobs.set(componentId, job);
      this._set(job, { phase: PHASE.CLOUD_GENERATING });
      this._cloudWatchLoop(job);
    }
    return job;
  }

  /**
   * A cancelled generation resets the component to its pristine upload state:
   * the source image and any server-produced derivatives go away (payload
   * refs first, then the stored files — they ride the epack download, so
   * orphans would reach every student device). gen3d params survive for the
   * next attempt; the `cancelled` tombstone still lands so other browsers'
   * loops detect the cancel fast and adoption stays blocked even if state
   * skews — the next upload clears it. img_url_3 is deliberately untouched:
   * cancel can race a landing model, and a model that made it should appear,
   * not be deleted behind the teacher's back.
   */
  async _wipeCancelledPayload(componentId) {
    const fileIds = [];
    await this._mergePayload(componentId, (p) => {
      ["img_url_1", "img_url_2", "img_url_4"].forEach((key) => {
        if (p[key] && p[key].file_id) fileIds.push(p[key].file_id);
        delete p[key];
      });
      delete p.self3d;
      p.gen3d = Object.assign({}, p.gen3d || {}, { cancelled: true });
    });
    // Files go only after the reference-free payload is the server's truth —
    // the reverse order could leave live refs pointing at deleted files.
    fileIds.forEach((fileId) => {
      this._api("delete_file", { file_id: fileId }).catch(() => {});
    });
  }

  /**
   * User-initiated cancel. Stops whatever is running and resets the component
   * to the pristine upload state (no model, no source image — see
   * _wipeCancelledPayload), so nothing auto-resumes it:
   *   * self task     → cancelled through the ev api (cooperative, best-effort)
   *   * cron row      → status 106 "cancelled" (works on an updated ev api;
   *                     an old server's cron cannot be stopped and may still
   *                     land a model later — harmless, it just appears)
   *   * component     → image/derivative refs + files removed, self3d removed,
   *                     payload.gen3d.cancelled = true
   *   * this registry → timers cleared, job dropped from map + localStorage
   * The panel then starts over with a fresh upload.
   */
  cancel(componentId) {
    componentId = parseInt(componentId);
    const job = this.jobs.get(componentId);
    if (!job) return;
    this._clearTimer(componentId);
    if (job.mode === "self" && job.taskId && !job.fellBack) {
      this._selfCancelRemote(job); // fire and forget
    }
    this._markGenItem(job, GEN_ITEM_CANCELLED).catch(() => {});
    this._wipeCancelledPayload(componentId).catch(() => {});
    // Notify with the terminal phase first (open panels reset to the pristine
    // upload state), then drop the job so indicator/persistence forget it.
    this._set(job, { phase: PHASE.CANCELLED, progress: null, message: "", error: "" });
    this.jobs.delete(componentId);
    this._persist();
    this._toast(str("GEN3D_CANCELLED", "3D generation cancelled."), true);
  }

  /**
   * The generation was cancelled OUTSIDE this registry — at the GPU platform
   * (an admin cancelled or deleted the task) or from another browser (seen via
   * the payload's cancelled flag). Mirror cancel()'s local teardown so the job
   * cannot survive as a zombie chip — and do NOT fall back to the cloud:
   * answering a deliberate cancel with a second, paid generation is exactly
   * the double-spend this pipeline exists to avoid.
   *   writeBack   — false when the payload already records the cancel (the
   *                 other browser wrote flag + parked the cron row); true when
   *                 this browser is the first to learn of it.
   *   cancelRemote — true only for the another-browser case, where the remote
   *                 task may still be running; platform-side cancels are
   *                 already terminal there.
   */
  _cancelledOutside(job, { toast = true, writeBack = true, cancelRemote = false } = {}) {
    this._clearTimer(job.componentId);
    if (cancelRemote && job.mode === "self" && job.taskId && !job.fellBack) {
      this._selfCancelRemote(job); // fire and forget
    }
    if (writeBack) {
      this._markGenItem(job, GEN_ITEM_CANCELLED).catch(() => {});
      this._wipeCancelledPayload(job.componentId).catch(() => {});
    }
    this._set(job, { phase: PHASE.CANCELLED, progress: null, message: "", error: "" });
    this.jobs.delete(job.componentId);
    this._persist();
    if (toast) {
      this._toast(str("GEN3D_CANCELLED_REMOTE", "3D generation was cancelled on the server."), true);
    }
  }

  /** Drop a job (component deleted / user replaced the image). */
  forget(componentId) {
    componentId = parseInt(componentId);
    const job = this.jobs.get(componentId);
    if (!job) return;
    this._clearTimer(componentId);
    if (job.mode === "self" && job.taskId && !job.fellBack) {
      this._selfCancelRemote(job);
    }
    this.jobs.delete(componentId);
    this._persist();
    this._notify(job);
  }

  /**
   * The component was deleted in THIS editor — tear its generation down
   * everywhere. Called BEFORE the delete request goes out, because the gen
   * item write authorizes against the component and cannot land afterwards.
   * Covers waiting AND processing jobs on every pipeline: the remote self
   * task is cancelled (forget), the cron row is parked on 106 so the Hunyuan
   * pipeline drops it, and registry/timers/localStorage are cleared.
   */
  handleComponentDeleted(componentId) {
    componentId = parseInt(componentId);
    const job = this.jobs.get(componentId);
    if (job) {
      this._markGenItem(job, GEN_ITEM_CANCELLED).catch(() => {});
    }
    this.forget(componentId);
  }

  /** Hotspot deletion cascades its components server-side — mirror it here. */
  handleHotspotDeleted(hotspotId) {
    hotspotId = parseInt(hotspotId);
    const ids = [];
    this.jobs.forEach((j) => {
      if (j.hotspotId != null && parseInt(j.hotspotId) === hotspotId) ids.push(j.componentId);
    });
    ids.forEach((id) => this.handleComponentDeleted(id));
  }

  /**
   * Zombie guard: the component vanished under a live job (deleted from
   * another browser, or a hotspot cascade we did not see). Cancel the remote
   * work and drop silently — there is nothing left to attach a result to.
   */
  _dropForDeletedComponent(job) {
    console.info("[3D] component", job.componentId, "no longer exists — dropping its job");
    this._clearTimer(job.componentId);
    if (job.mode === "self" && job.taskId && !job.fellBack) {
      this._selfCancelRemote(job);
    }
    this.jobs.delete(job.componentId);
    this._persist();
    this._notify(job);
  }

  // ---------------------------------------------------------- reconciliation

  /**
   * Ask the server what it knows about this job's row/task and settle the
   * chip when the answer is terminal. Covers the signals the component
   * payload alone cannot carry: task cancelled or deleted AT the platform,
   * row parked on 106 by another client/admin, row missing entirely (nothing
   * will ever generate it). Returns true when the job was settled, false to
   * keep watching; inconclusive (old server without the route, transient
   * error) also returns false — never drop a chip on a guess.
   */
  async _checkRowSignals(job) {
    let res;
    try {
      res = await this._api("get_3d_self_status", { component_id: job.componentId });
    } catch (e) {
      return false;
    }
    if (!this.jobs.has(job.componentId)) return true;
    const data = res && res.body && res.body.result ? res.body.result : {};
    const task = data.task || null;
    if (task) {
      const cancelText = String(task.error || "") + " " + String(task.media_message || "");
      if (task.status === "gone" || task.status === "cancelled" ||
          (task.status === "failed" && /cancel/i.test(cancelText))) {
        this._cancelledOutside(job, { toast: false });
        return true;
      }
      return false; // genuinely live (failures are the poll loop's business)
    }
    const rowStatus = data.row_status != null ? String(data.row_status) : "missing";
    if (rowStatus === GEN_ITEM_CANCELLED) {
      this._cancelledOutside(job, { toast: false });
      return true;
    }
    // The v2 server tracks every queued generation in a row; no row means
    // nothing will ever produce this model. Two guards: a fallen-back job's
    // _ensureCloudFlip may still be about to INSERT its row, and a freshly
    // started cloud job's insert may be in flight.
    if (rowStatus === "missing" && !job.fellBack && (now() - job.startedAt) > 120000) {
      this._cancelledOutside(job, { toast: false });
      return true;
    }
    return false;
  }

  /**
   * Reconcile ONE job with server truth: component gone → drop; model landed
   * → finish; cancelled flag → drop; then the row/task signals above.
   * Chips restored from localStorage can describe work that ended while no
   * browser was open (a platform-side cancel, another machine's cleanup) —
   * the watch loops alone would spin on those for hours.
   */
  async _validateJob(job) {
    if (!this.jobs.has(job.componentId)) return true;
    let item;
    try {
      item = await this._getComponent(job.componentId);
    } catch (e) {
      return false;
    }
    if (!this.jobs.has(job.componentId)) return true;
    if (item === null) {
      this._dropForDeletedComponent(job);
      return true;
    }
    const payload = this._parsePayload(item.payload);
    if (payload.img_url_3 && payload.img_url_3.path) {
      this._finishJob(job);
      return true;
    }
    if (payload.gen3d && payload.gen3d.cancelled) {
      this._cancelledOutside(job, { toast: false, writeBack: false });
      return true;
    }
    return this._checkRowSignals(job);
  }

  /**
   * Sweep every tracked job against server truth; resolves the number of
   * chips settled. Runs at resume() and behind the indicator's clean-up
   * action. Sequential on purpose — a login-time burst of 2×N calls for a
   * cosmetic sweep would be rude to the api.
   */
  async revalidate() {
    let settled = 0;
    for (const job of Array.from(this.jobs.values())) {
      try {
        if (await this._validateJob(job)) settled += 1;
      } catch (e) { /* keep the job */ }
    }
    return settled;
  }

  // ------------------------------------------ server-mediated platform calls
  // The browser holds NO platform credential: submission, status, artifact and
  // cancel all go through the ev api (/api/ai3d/*), which talks to the GPU
  // platform with the SERVER's identity, checks quota BEFORE submitting, and
  // records the job row — the server remembers the state from second one.

  async _selfSubmit(job, prompt, params) {
    const p = params || {};
    const res = await this._api("generate_3d_self", Object.assign({
      component_id: job.componentId,
      prompt: String(prompt || "").slice(0, 300),
      engine: typeof p.engine === "string" && p.engine !== "" ? p.engine : "trellis-2",
      quality: typeof p.quality === "string" ? p.quality : "high",
    }, this._genItemParams(p)));
    const data = res && res.body && res.body.result ? res.body.result : {};
    if (!data.task_id) throw new Error("generate did not return a task_id");
    return { taskId: data.task_id };
  }

  /** Status via the server; shaped like the platform task the poll loop
   * expects ({status, progress, error, media_job:{message}, has_output}),
   * plus status "gone"/"unreachable" markers from the proxy. */
  async _selfStatus(job) {
    const res = await this._api("get_3d_self_status", { component_id: job.componentId });
    const data = res && res.body && res.body.result ? res.body.result : {};
    const task = data.task;
    if (!task) {
      // Row left 100 without this browser seeing it (another client or the
      // backstop worker closed it) — let the payload/cloud watch settle it.
      return { status: "row:" + String(data.row_status || "missing") };
    }
    return {
      status: task.status,
      progress: task.progress,
      error: task.error,
      has_output: !!task.has_output,
      media_job: { message: task.media_message || "" },
    };
  }

  /** Cancel through the server (platform cancel + row parked on 106). */
  async _selfCancelRemote(job) {
    try {
      await this._api("cancel_3d_self", { component_id: job.componentId });
      return true;
    } catch (e) {
      console.warn("[3D] server-side cancel failed", job.componentId, e);
      return false;
    }
  }

  /** Download the finished GLB THROUGH the ev api (same origin, editor JWT). */
  async _selfDownloadArtifact(job) {
    const url = AuthService.apiUrl("api/ai3d/genItem/" + encodeURIComponent(job.componentId) + "/artifact");
    const resp = await AuthService.authFetch("GET", url);
    if (!resp || !resp.ok) {
      throw new Error("artifact HTTP " + (resp ? resp.status : "network"));
    }
    const blob = await resp.blob();
    GlbOptimizer.validateGlbBytes(new Uint8Array(await blob.arrayBuffer()));
    return blob;
  }

  // ------------------------------------------------------------ self pipeline

  async _runSelfPipeline(job, imageFileInfo, prompt, params) {
    if (!imageFileInfo || !imageFileInfo.path) {
      this._fallbackToCloud(job, "no source image", params);
      return;
    }

    // ONE server call does it all: quota check (409 when none), source image
    // fetch, platform submit under the SERVER's identity, and the
    // ai_3d_gen_item row (status 100 + task handle) — the DB is the system of
    // record before this promise even resolves. Any error (409 no quota,
    // 502 platform down, 503 unconfigured, 404 on an old server) lands in
    // catch and takes the standard cloud fallback.
    try {
      const { taskId } = await this._selfSubmit(job, prompt, params);
      if (!this.jobs.has(job.componentId)) {
        this._selfCancelRemote(job); // forgotten while submitting
        return;
      }
      job.taskId = taskId;
      job.claimed = true; // the server created the row
      this._set(job, { phase: PHASE.QUEUED });
      // payload.self3d is what other panels/browsers adopt from; best effort.
      this._mergePayload(job.componentId, (p) => {
        p.self3d = { task_id: taskId, submitted_at: job.startedAt };
      }).catch(() => {});
      this._selfPollLoop(job);
    } catch (e) {
      // A QUOTA refusal is the one error that must not fall back. The cloud
      // pipeline bills a real generation, and the cron's redeem proceeds even
      // with no row to spend ("no quota deducted"), so falling back would let
      // a school at zero keep generating — at full cost — while the editor
      // showed a normal progress bar. Fail the job with the server's reason,
      // and still claim the ai_3d_gen_item row: without one, the ADD cron
      // discovers the component (image, no model, no row) and generates it in
      // the cloud anyway, which is the same bypass by another route.
      if (this._isQuotaRefusal(e)) {
        console.warn("3D self submit refused: no quota");
        this._markGenItem(job, GEN_ITEM_CANCELLED).catch(() => {});
        this._set(job, {
          phase: PHASE.FAILED,
          progress: null,
          error: str("NO_3D_QUOTA_CONTACT_ADMIN", "No 3D model quota available. Please contact your administrator."),
        });
        return;
      }
      this._fallbackToCloud(job, this._errText(e) || "submit failed", params);
    }
  }

  /** Text of a rejected _api() call — those reject with the RESPONSE object
   *  ({status, body:{remarks}}), not an Error, so `e.message` is undefined. */
  _errText(e) {
    if (!e) return "";
    if (e.body && e.body.remarks) return String(e.body.remarks);
    if (e.message) return String(e.message);
    return "";
  }

  /** Did the server refuse for lack of quota? /api/ai3d/generate answers 409
   *  "No 3D model quota available"; the status alone is not enough because a
   *  409 can also mean a competing claim on the row. */
  _isQuotaRefusal(e) {
    if (!e) return false;
    const text = this._errText(e).toLowerCase();
    return text.includes("quota");
  }


  _selfPollLoop(job) {
    const tick = async () => {
      if (!this.jobs.has(job.componentId)) return;
      const elapsed = now() - job.startedAt;
      if (elapsed > SELF_TIMEOUT_MS) {
        this._selfCancelRemote(job);
        this._fallbackToCloud(job, "self host timeout");
        return;
      }

      // Every few ticks, confirm the component is still there: a deletion in
      // another browser (or a hotspot cascade) must not leave this loop — and
      // the GPU job behind it — running as a zombie.
      job._tickCount = (job._tickCount || 0) + 1;
      if (job._tickCount % COMPONENT_CHECK_EVERY === 0) {
        try {
          const item = await this._getComponent(job.componentId);
          if (item === null) {
            this._dropForDeletedComponent(job);
            return;
          }
          const freshPayload = this._parsePayload(item.payload);
          if (freshPayload.gen3d && freshPayload.gen3d.cancelled) {
            // Cancelled from another browser: it wrote the flag and parked the
            // cron row, and it toasted its own user — here the job just goes
            // away. cancelRemote because ITS server-side cancel may have failed
            // and this browser holds the taskId too; a duplicate cancel is a
            // harmless 409.
            this._cancelledOutside(job, { toast: false, writeBack: false, cancelRemote: true });
            return;
          }
        } catch (e) { /* transient read failure — keep polling */ }
        if (!this.jobs.has(job.componentId)) return;
      }

      let task = null;
      try {
        task = await this._selfStatus(job);
        job._pollErrors = 0;
      } catch (e) {
        job._pollErrors = (job._pollErrors || 0) + 1;
        if (job._pollErrors >= MAX_POLL_ERRORS) {
          this._fallbackToCloud(job, "status route unreachable");
          return;
        }
        this._set(job, { slow: elapsed > SLOW_AFTER_MS });
        this._schedule(job, tick, SELF_POLL_MS);
        return;
      }

      // "gone" = the platform DELETED a task we submitted. That is a cancel,
      // not an outage — falling back would regenerate work someone just
      // killed. "unreachable" = the server could not reach the platform:
      // transient, count it like a poll error. "row:*" = another client (or
      // the backstop worker) already moved the row out of 100 — the payload
      // watch settles what actually happened.
      if (task.status === "gone") {
        this._cancelledOutside(job);
        return;
      }
      if (task.status === "unreachable" || String(task.status || "").indexOf("row:") === 0) {
        job._pollErrors = (job._pollErrors || 0) + 1;
        if (job._pollErrors >= MAX_POLL_ERRORS || String(task.status || "").indexOf("row:") === 0) {
          // The row moved on without us: adopt the outcome via the cloud
          // watch (it finishes on payload truth or keeps waiting).
          this._set(job, { fellBack: job.fellBack, phase: PHASE.CLOUD_GENERATING });
          this._cloudWatchLoop(job);
          return;
        }
        this._set(job, { slow: elapsed > SLOW_AFTER_MS });
        this._schedule(job, tick, SELF_POLL_MS);
        return;
      }

      const media = task && task.media_job ? task.media_job : {};
      const sig = String(task.status) + "|" + String(task.progress) + "|" + String(media.message || "");
      if (sig !== job._progressSig) {
        job._progressSig = sig;
        job._sigSince = now();
      } else if (job._sigSince && (now() - job._sigSince) > STALL_TIMEOUT_MS && task.status !== "queued") {
        this._selfCancelRemote(job);
        this._fallbackToCloud(job, "self host stalled");
        return;
      }

      // Cancelled at the platform (its own UI/admin): mirror the cancel here
      // instead of falling back — the chip disappears and nothing regenerates.
      // The platform words a user cancel as failed + "Media generation
      // cancelled" (task.status may or may not say "cancelled"), so match the
      // text as well; when in doubt, honouring a cancel beats double-spending.
      const cancelText = String(task.error || "") + " " + String(media.message || "");
      if (task.status === "cancelled" || (task.status === "failed" && /cancel/i.test(cancelText))) {
        this._cancelledOutside(job);
        return;
      }
      if (task.status === "failed") {
        this._fallbackToCloud(job, task.error || "task failed");
        return;
      }

      if (task.status === "completed") {
        if (!task.has_output) {
          this._fallbackToCloud(job, "no model in task outputs");
          return;
        }
        this._importSelfResult(job);
        return;
      }

      const phase =
        task.status === "queued" ? PHASE.QUEUED :
        (task.status === "planning" || task.status === "preparing_environment") ? PHASE.PREPARING :
        task.status === "verifying" ? PHASE.FINALIZING :
        PHASE.GENERATING;
      const progress = typeof task.progress === "number" && task.progress > 0 ? Math.min(95, task.progress) : null;
      this._set(job, { phase, progress, message: publicSelfMessage(media.message), slow: elapsed > SLOW_AFTER_MS });
      this._schedule(job, tick, SELF_POLL_MS);
    };
    this._schedule(job, tick, 100);
  }

  async _importSelfResult(job) {
    this._set(job, { phase: PHASE.IMPORTING, progress: 96, message: "" });

    // The teacher's quality choice lives on the component (survives reloads
    // and lets any browser that adopts the job finish it the same way). The
    // same read doubles as the deleted-component check: importing a model for
    // a component that no longer exists would only manufacture orphan files.
    let quality = "high";
    try {
      const item = await this._getComponent(job.componentId);
      if (item === null) {
        this._dropForDeletedComponent(job);
        return;
      }
      const freshPayload = this._parsePayload(item.payload);
      if (freshPayload.img_url_3 && freshPayload.img_url_3.path) {
        // Another browser adopted the same task and already landed the model
        // (both can legitimately watch one payload.self3d task). Importing
        // again would upload a duplicate file and redeem quota twice — the
        // job is simply done.
        console.info("[3D] component", job.componentId, "already has a model — skipping duplicate import");
        this._finishJob(job);
        return;
      }
      const gen3d = freshPayload.gen3d || {};
      if (typeof gen3d.quality === "string") quality = gen3d.quality;
    } catch (e) { /* default stands */ }
    if (!this.jobs.has(job.componentId)) return;

    for (let attempt = 1; attempt <= IMPORT_ATTEMPTS; attempt++) {
      try {
        const rawBlob = await this._selfDownloadArtifact(job);
        // Cancel may have landed while downloading — honour it before the
        // model reference is written (a cancel arriving later than this
        // simply loses the race and the finished model shows up).
        if (!this.jobs.has(job.componentId)) return;

        // Shrink before storing (ev-world's pipeline, ported): normals +
        // simplify + texture re-encode. Degrades to the raw bytes on any
        // failure — a heavy model beats a lost one.
        this._set(job, { message: str("GEN3D_OPTIMIZING", "Compressing the model...") });
        const { blob, stats } = await GlbOptimizer.optimizeGeneratedGlb(rawBlob, quality);
        console.info("[3D] optimize", quality, stats.stage,
          Math.round(stats.sourceBytes / 1024) + "KB -> " + Math.round(stats.outputBytes / 1024) + "KB",
          "tris", stats.triangles, "->", stats.outputTriangles);
        if (!this.jobs.has(job.componentId)) return;
        this._set(job, { message: "" });

        const fileInfo = await this._uploadGlbToStorage(job, blob, "model_" + job.componentId + ".glb");
        if (!this.jobs.has(job.componentId)) return;
        await this._mergePayload(job.componentId, (p) => {
          p.img_url_3 = fileInfo;
          delete p.self3d;
        });

        // Close the job on the server: one PATCH moves the row to 105 AND
        // spends the quota (the route redeems when file_id rides along) — no
        // separate redeem call for a dying browser to skip. Best-effort: on
        // an old server the cron's sweep closes the row from the payload.
        this._markGenItem(job, GEN_ITEM_SELF_DONE, "", null, { file_id: fileInfo.file_id }).catch(() => {});

        this._finishJob(job);
        return;
      } catch (e) {
        console.warn("3D import attempt " + attempt + " failed", e);
        if (attempt === IMPORT_ATTEMPTS) {
          // The GPU result exists but we cannot land it — let the cloud
          // pipeline regenerate rather than leaving the component stuck.
          this._fallbackToCloud(job, "import failed: " + (e && e.message ? e.message : ""));
          return;
        }
        await new Promise((r) => setTimeout(r, 3000 * attempt));
        if (!this.jobs.has(job.componentId)) return;
      }
    }
  }

  /** request_upload → PUT to SAS → confirm_upload, shaped like FilePicker's result. */
  async _uploadGlbToStorage(job, blob, filename) {
    const baseName = String(filename || "model.glb").replace(/\.glb$/i, "");
    const reqRes = await this._api("request_upload", {
      user_id: this.root.getMyUserId(),
      epack_id: job.epackId || 0,
      files: [{
        filename: baseName + ".glb",
        extension: "glb",
        file_size: blob.size,
        needs_thumbnail: 0,
        thumbnail_extension: "",
        thumbnail_file_size: 0,
      }],
    });
    const reg = reqRes && reqRes.body && reqRes.body.result && Array.isArray(reqRes.body.result.files)
      ? reqRes.body.result.files[0] : null;
    if (!reg || reg.error || !reg.sas_url) {
      throw new Error((reg && reg.error) || "request_upload failed");
    }
    await axios.put(reg.sas_url, blob, {
      headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": "model/gltf-binary" },
    });
    const confRes = await this._api("confirm_upload", { file_ids: [reg.file_id] });
    const confirmed = confRes && confRes.body && confRes.body.result && Array.isArray(confRes.body.result.files)
      ? confRes.body.result.files[0] : null;
    if (!confirmed || confirmed.error) {
      throw new Error((confirmed && confirmed.error) || "confirm_upload failed");
    }
    return {
      ok: confirmed.ok != null ? confirmed.ok : 1,
      file_id: confirmed.file_id,
      display_name: confirmed.display_name || baseName,
      path: confirmed.path || "",
      tn_path: confirmed.tn_path || "",
      file_type: confirmed.file_type || "glb",
      file_size: confirmed.file_size != null ? confirmed.file_size : blob.size,
      message: "generated by built-in 3D engine",
    };
  }

  // -------------------------------------------------------------- cloud path

  /**
   * Self pipeline failed somewhere: hand the component to the Hunyuan cron by
   * flipping (or creating) its gen item with status -1, then watch the payload
   * like a plain cloud job. The flip retries inside the watch loop until it
   * lands, so a transient API error cannot strand the component.
   */
  _fallbackToCloud(job, reason, params) {
    if (!this.jobs.has(job.componentId)) return;
    console.warn("3D self pipeline fell back to cloud:", reason);
    job._fallbackParams = params || job._fallbackParams || null;
    this._set(job, {
      fellBack: true,
      flipDone: false,
      phase: PHASE.CLOUD_GENERATING,
      progress: null,
      message: "",
      error: "",
    });
    // The reason is diagnostics ("self host stalled", a vendor error body) —
    // console only. The toast says what the teacher needs: it fell back.
    this._toast(str("GEN3D_FALLBACK_NOTICE", "Built-in engine unavailable — switched to cloud generation."), true);
    this._cloudWatchLoop(job);
  }

  async _ensureCloudFlip(job) {
    if (job.flipDone || !job.fellBack) return;
    try {
      const item = await this._getComponent(job.componentId);
      const payload = item ? this._parsePayload(item.payload) : {};
      const img1 = payload.img_url_1;
      const imgUrlAbs = img1 && img1.path ? Config.UPLOAD_PATH() + img1.path : "";
      await this._markGenItem(job, GEN_ITEM_QUEUE_FOR_CLOUD, imgUrlAbs, job._fallbackParams || (payload.gen3d || null));
      job.flipDone = true;
    } catch (e) {
      // Route missing (older server): if we never claimed, the cron's own
      // discovery will pick the component up; if we DID claim, keep retrying —
      // the claim row blocks the cron until the flip lands.
      if (!job.claimed) job.flipDone = true;
    }
  }

  _cloudWatchLoop(job) {
    const tick = async () => {
      if (!this.jobs.has(job.componentId)) return;
      const elapsed = now() - job.startedAt;

      // Ceiling: after this long the cron has either died or terminally
      // rejected the item (a state this side cannot read). Stop watching and
      // surface a retryable failure instead of spinning forever — the
      // component keeps its image, 重新生成 starts clean.
      if (elapsed > CLOUD_WATCH_MAX_MS) {
        this._clearTimer(job.componentId);
        this._set(job, { phase: PHASE.FAILED, error: str("GEN3D_PHASE_FAILED", "Generation failed"), progress: null });
        this.jobs.delete(job.componentId);
        this._persist();
        return;
      }

      await this._ensureCloudFlip(job);
      try {
        const item = await this._getComponent(job.componentId);
        if (!item) {
          this._dropForDeletedComponent(job);
          return;
        }
        const payload = this._parsePayload(item.payload);
        if (payload.img_url_3 && payload.img_url_3.path) {
          // The payload is the server's word that generation finished. The
          // reachability probe only papers over the half-written-upload edge —
          // it must never OVERRULE the payload, because some browsers (Safari)
          // fail cross-origin HEADs to the blob host outright and would watch
          // a finished model forever. Three failed probes: server truth wins.
          if (await this._probeModel(payload)) {
            this._finishJob(job);
            return;
          }
          job._probeFails = (job._probeFails || 0) + 1;
          if (job._probeFails >= 3) {
            console.warn("[3D] model present but unreachable from this browser — finishing on payload truth");
            this._finishJob(job);
            return;
          }
        }
        // No model yet and the payload says cancelled (another browser's
        // cancel(), which also parked the cron row): stop watching — otherwise
        // this chip spins for the full 8h ceiling on a job nobody is running.
        // Checked AFTER img_url_3 so a model that raced the cancel and landed
        // anyway still finishes as DONE.
        if (payload.gen3d && payload.gen3d.cancelled) {
          this._cancelledOutside(job, { toast: false, writeBack: false });
          return;
        }
      } catch (e) {
        // Transient read failure: keep watching.
      }
      // The payload cannot say "cancelled at the platform" or "row parked by
      // another machine" — ask the server directly, on a slow cadence.
      job._rowCheckTick = (job._rowCheckTick || 0) + 1;
      if (job._rowCheckTick % CLOUD_ROW_CHECK_EVERY === 0) {
        if (await this._checkRowSignals(job)) return;
        if (!this.jobs.has(job.componentId)) return;
      }
      this._set(job, { slow: elapsed > SLOW_AFTER_MS });
      // Long waits back off to a polite cadence — the answer will not change
      // five times a second after the first hour.
      this._schedule(job, tick, elapsed > CLOUD_POLL_SLOW_AFTER_MS ? CLOUD_POLL_SLOW_MS : CLOUD_POLL_MS);
    };
    this._schedule(job, tick, 100);
  }

  // ------------------------------------------------------------------ finish

  _finishJob(job) {
    this._clearTimer(job.componentId);
    this._set(job, { phase: PHASE.DONE, progress: 100, slow: false });
    const name = job.name || str("MODEL_3D_DEFAULT_NAME", "3D Model");
    this._toast(name + " — " + str("GEN3D_READY_NOTICE", "3D model is ready."), true);
    // Keep the finished entry around briefly so an open panel can render the
    // done state, then drop it from the registry (and localStorage).
    setTimeout(() => {
      if (this.jobs.get(job.componentId) === job && job.phase === PHASE.DONE) {
        this.jobs.delete(job.componentId);
        this._persist();
        this._notify(job);
      }
    }, 60000);
  }
}

const ThreeDJobManager = new ThreeDJobManagerClass();
export default ThreeDJobManager;
